import _ from "underscore";
import DriveView from "../../DriveView";
import ViewDvirView from "./view";
import DVIRLog, { IDefectRemark, IDvirDefects, TRepairStatus, TLogType, REPAIR_STATUS_STRINGS } from "../../model/DVIRLog";
import { Device } from "../../model/Device";
import { TrailerAttachments } from "../../model/TrailerAttachments";
import Sync from "../../sync/sync";
import { SyncType } from "../../sync/sync.types";
import { populateAddressesRedux } from "../../sync/common";
import Api from "../../services/api";
import { getCurrentVehicle } from "../../utils/device";
import { BaseCache, makeDeviceCache } from "../../utils/storage/cache";
import { CancelablePromise as Promise } from "../../utils/cancelablePromise";
import { formInspectedVehicleString, translateDefectListName, getDefectNames, getLabel, getDefectListForDefect, getDeviceLicense, isFromCanada, isFromAustralia } from "./helper";
import { getServerDate } from "../../utils/storage/date";
import toastr from "../../utils/toastr";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import { hide, htmlEscape, createElement, show } from "../../utils/vanilla";
import { AuditType, auditSkip } from "../../utils/auditHelper";
import { geocodeAddresses, getCurrentLocationUsingDeviceLocationAndGps } from "../../utils/location";
import { handleApi } from "../../utils/error";
import { Dialog } from "../framework/dialog";
import InspectionChecklist from "./inspectionList";
import { getDefectListsForAsset } from "../../utils/defectList";
import { dateTimeComparator, dstCalculator } from "../../utils/date";
import { PageLevel } from "../../store/mobile/types";
import idSerializer from "geotab-uuid";
import { isCameraPluginPresent } from "../../utils/native/nativeFileApi";
import { openPictureOptionDialog, updateCameraIconWithImage, getCachedImageForRemark } from "./imageHelper";
import { IImageOptions, deleteImage } from "../../model/Image";
import pageHTML from "./inspect.tmpl";
import repairPartHTML from "./repair-log.tmpl";
import repairDefectHTML from "./repair-log-defect.tmpl";
import logAddressHTML from "../framework/addressDialog.tmpl";
import User from "../../model/User";
import { renderCheckbox } from "../framework/checkbox";
import { MobileActions } from "../../store/mobile/actions";
import constants from "../../constants";
import { IApiError } from "../../utils/api.interfaces";
import { IDefect } from "../../model/Defect";
import { isNetworkError } from "../../utils/errorHandler/error.types";
import { timer, Source, error as LogError } from "../../utils/logger";
import { OpenCabWorkflowActions } from "../../store/openCabWorkflow/actions";
import { getDisplayTimeSpan } from "../../utils/helpers";
import { msToTimespan } from "hos-tools";
import dayjs from "../../utils/dayjs";
import DutyStatusLog from "../../model/DutyStatusLog";
import { getStatusData, getRawOdometer, hoursToSeconds, secondsToHours } from "../../utils/helpers";

const MAX_DEFECTS_OPEN_BY_DEFAULT = 4; // Expand repair dropdown by default if defect count is this number or less, otherwise all collapsed

interface ILatestStatusData {
    odometer?: number | null;
    engineHours?: number | null;
}

export default class InspectDvirView extends DriveView<any> {

    private static readonly remarkLengthErrorKeys: Record<string, string> = {
        // DefectRemarkBridge rejects defect remarks with an un-prefixed message, unlike the DVIR log fields below.
        "Remark must not exceed 500 characters.": "Remark must not exceed 500 characters",
        "DVIR:DriverRemark must not exceed 500 characters.": "Remark must not exceed 500 characters",
        "DVIR:RepairRemark must not exceed 500 characters.": "Repair remark must not exceed 500 characters",
        "DVIR:CertifyRemark must not exceed 500 characters.": "Certification remark must not exceed 500 characters"
    };

    model: DVIRLog;

    protected strings = [
        "Submit",
        "Submit (no defects)",
        "No defects",
        "Save",
        "Saving in progress",
        "Your changes have been saved.",
        "Inspection complete",
        "Your current location could not be detected. Please enter your address.",
        // Toast text for handleRemarkLengthError. These must be registered here or getString()
        // never fetches a translation and returns "**<English>" verbatim to non-English drivers.
        "Remark must not exceed 500 characters",
        "Repair remark must not exceed 500 characters",
        "Certification remark must not exceed 500 characters",
        "Not repaired",
        "Repair not required",
        "Repaired",
        "No changes",
        "Inspection accepted.",
        "Inspection rejected.",
        "{0} defects",
        "{0} defect",
        "Other",
        "You must select a defect or check off 'No defect' if there are no defects for mandatory items.",
        "A new inspection has been logged for this asset.",
        "Unknown",
        "Duration",
        "d",
        "h",
        "{minutes}m",
        "s",
        "Inspection done in {0}. You're {1} as of {2}.",
        "On Duty",
        "Inspection done in {0}.",
        "You have been placed on duty.",
        "Add a remark",
        "Add a remark for {0}",
        "Existing defects dropdown for {0}"
    ];
    private readonly template = _.template(pageHTML);
    private readonly repairPartTemplate = _.template(repairPartHTML);
    private readonly repairDefectTemplate = _.template(repairDefectHTML);
    private readonly logAddressInputTemplate = _.template(logAddressHTML);
    private readonly sync: Sync;
    private readonly api: Api;
    private readonly deviceCache: BaseCache<Device | IDevice>;
    private readonly workflow: boolean;
    private readonly currentUser: DeepReadonlyObject<IUser> | null;
    private readonly trailerAttachmentCollection: TrailerAttachments;
    private readonly dvirStartTime: Date;
    private readonly isDetach: boolean;
    private readonly isTrailerWorkflow: boolean;
    private readonly trailerAttachment: string;
    private readonly defectListsDict: { [id: string]: IDefect } = {};
    private readonly isMetric?: boolean;
    private readonly populatedDefectNames: { [id: string]: string } = {};
    private readonly showCameraIcons: boolean;
    private readonly canUseMedia: boolean;
    private readonly canViewMedia: boolean;
    private mediaFileLoadFunctions: (() => void)[];
    private defectLists: IDefect[];
    private currentDefectList: IDefect;
    private viewDvirView: ViewDvirView | null;
    private newInspection: DVIRLog;
    private shouldDoAuditOnRemove: boolean;
    private inspectionChecklist: InspectionChecklist | null;
    private completedDvir = false;
    private newAddress;
    private userInputCoordinates: any = {};
    private inspectionDialog: Dialog;
    private addressDialog: Dialog;
    private isCanada?: boolean;
    private isAustralia?: boolean;
    private dvirDurationStartTime: Date;
    private readonly selectiveSync: Set<SyncType> = new Set();

    /*
     * Initialize the view, set up a new dvir report, precompile the template, bind model change to render event
     */
    constructor(options) {
        super(options, {
            title: "Asset Inspection",
            activeSection: "dvir",
            pageLevel: options.pageLevel || PageLevel.TWO
        });
        this.sync = this.app.sync;
        this.api = this.app.api;
        this.deviceCache = makeDeviceCache(this.app.store);
        if (!options.state) {
            return;
        }

        this.workflow = options.workflow || false;
        this.isTrailerWorkflow = options.state.detach !== undefined;
        this.trailerAttachment = options.state.trailerAttachment;
        this.isDetach = options.state.detach;
        this.shouldDoAuditOnRemove = true;

        this.trailerAttachmentCollection = new TrailerAttachments(this.api, this.app.store);

        this.currentUser = this.app.userService.getCurrentUser();
        this.isMetric = this.currentUser && this.currentUser.isMetric !== undefined ? this.currentUser.isMetric : true;
        this.dvirStartTime = getServerDate();

        let id: string | undefined = options.state.id; // passing ID means loading an existing DVIR
        let assetId: string | undefined = options.state.assetId; // passing assetId means asset has no existing DVIRs

        // Early exits in the constructor don't exit the page, but we can't call certain methods if we're missing the id...
        if (!id && !assetId) {
            return;
        }

        if (id) {
            this.model = new DVIRLog({ id: id });
            this.fetchAndPopulate(this.model);

            if (!this.model.isActive()) {
                return;
            }

            this.viewDvirView = new ViewDvirView({ app: this.app, state: { id } });
            this.listenTo(dispatcher, DispatcherEvents.DvirUpdatedAddress, () => {
                this.fetchAndPopulate(this.model);
                this.render();
            });

            if (this.model.isComplete(this.app)) {
                this.createNewDvir();
                this.populateFromExisting();
            }
        } else if (assetId) {
            this.createNewDvir();
            this.populateAssets(assetId, this.newInspection);
            this.updatePageStateBasedOnVehicle();
        }
        this.listenTo(dispatcher, DispatcherEvents.UpdateCameraIcon, (imageOptions: IImageOptions) => {
            updateCameraIconWithImage(imageOptions, this.app.store, this.translator, this.isTrailerWorkflow || (this.model && this.model.getAssetType() === "Trailer"));
        });

        this.listenTo(dispatcher, DispatcherEvents.SyncDvir, () => {
            let existingIncompleteDvir = this.getExistingIncompleteDvir();
            if (existingIncompleteDvir && (!id || existingIncompleteDvir.id !== id) && existingIncompleteDvir.id !== this.newInspection?.id) {
                // If a new record was created that requires certification or repair, kick user out of current inspection
                toastr.info(
                    this.getString("A new inspection has been logged for this asset."),
                    undefined,
                    undefined,
                    {
                        preventDuplicates: true,
                        timeOut: constants.HOS.TOAST_MESSAGE_TIMEOUT
                    }
                );
                this.goBack(existingIncompleteDvir.id);
            }
        });

        this.mediaFileLoadFunctions = [];

        this.showCameraIcons = this.checkShowCameraIcons();
        this.canViewMedia = this.app.securityService.can("viewMedia", this.currentUser?.id);
        this.canUseMedia = this.canViewMedia && this.app.securityService.can("manageMedia", this.currentUser?.id);

        this.updatePageStateBasedOnVehicle();

        // make sure not to create audit log in remove method when the lock screen is about to appear
        this.listenTo(dispatcher, DispatcherEvents.LockscreenWillToggle, () => {
            this.shouldDoAuditOnRemove = false;
        });
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click .certify": this.repairAndCertify,
            "click #new-inspection": this.createNewInspection,
            "click #editInspectionLocation": this.addressInput,
            "click #save-repair": this.repair,
            "change .repair-selection-container .drive-checkbox__input": this.updateRepairStatus,
            "change #inspectionDefectListSelection": this.updateInspectionList,
            "input .repair-user-input.newRemark": this.typeRepairRemark,
            "change input[type='radio']": this.logInteraction,
            "click #changeAsset": this.changeAsset
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    createNewDvir() {
        this.dvirDurationStartTime = getServerDate();
        this.app.store.dispatch(MobileActions.setDvirDurationStartTime(this.dvirDurationStartTime));
        if (!this.newInspection) {
            this.newInspection = new DVIRLog({
                id: idSerializer.get(),
                driver: this.currentUser as IUser
            });
        }
    }

    getDvirLocation() {
        let location: IAddressLookupData | undefined;
        return getCurrentLocationUsingDeviceLocationAndGps(this.api, this.app.store)
            .then(addressCoordinates => {
                location = addressCoordinates;
                if (!location || !location.location) {
                    return Promise.resolve([]);
                }
                return populateAddressesRedux(this.api, this.app.store, [{ id: this.newInspection.id, coordinate: location.location }], true);
            })
            .timeout(constants.DVIR.GEOLOCATION_API_TIMEOUT)
            .then(([address]) => {
                let formattedAddress: string | null = address && address.formattedAddress || null;
                let region: string | null = address && address.region || null;
                let country: string | null = address && address.country || null;

                // We can only return a result if we have both.
                if (formattedAddress && location) {
                    return {
                        location,
                        formattedAddress,
                        region,
                        country
                    };
                }
                return null;

            })
            .catch(e => {
                let isSilentTimeout = e?.message?.toLowerCase().indexOf("timed out in") !== -1;
                if (isSilentTimeout) {
                    LogError(Source.ERROR, `Timed out trying to get the location for a new asset inspection. Error: ${ e.message }`);
                } else {
                    handleApi(e);
                }
                return null;
            });
    }

    getLatestStatusData(): Promise<ILatestStatusData | null> {
        const currentDevice = getCurrentVehicle()?.id || null;
        const currentDate = getServerDate();

        return Promise.all([
            getRawOdometer(this.app.api, currentDevice, [currentDate]),
            getStatusData(this.app.api, currentDevice, currentDate, currentDate, "DiagnosticEngineHoursAdjustmentId")
        ]).then(([odometerData, engineHoursData]) => {
            const odometer = odometerData[0] && odometerData[0].valueInMeters ? odometerData[0].valueInMeters : null;
            const engineHours = engineHoursData[0] && engineHoursData[0].data ? engineHoursData[0].data : null;
            return { odometer, engineHours };
        }).catch(() => {
            return null;
        });
    }

    disableUIActions(disable = true) {
        this.disableButton("editInspectionLocation", disable);
        this.disableButton("new-inspection", disable);
        this.disableButton("odometerInput", disable);
        this.disableButton("engineHoursInput", disable);
    }

    populateCurrentAddressAndOtherInfo() {
        if (this.model && !this.model.isComplete(this.app)) {
            return undefined;
        }

        const inspectionAddress = document.getElementById("inspection-address") as HTMLInputElement;
        let addressPopulated = false;
        const inspectionOdometer = document.getElementById("odometerInput") as HTMLInputElement;
        const inspectionEngineHours = document.getElementById("engineHoursInput") as HTMLInputElement;
        const needPopulateOtherInfo = !!inspectionOdometer && !!inspectionEngineHours;

        if (this.newAddress?.formattedAddress && this.newAddress.location) {
            if (inspectionAddress) {
                inspectionAddress.textContent = this.newAddress.formattedAddress;
            }
            addressPopulated = true;
        }

        if (needPopulateOtherInfo && this.newInspection?._odometer && this.newInspection?._engineHours) {
            inspectionOdometer && (inspectionOdometer.value = this.app.localizerService.toLocalDistance(this.newInspection._odometer, true).toFixed(1));
            inspectionEngineHours && (inspectionEngineHours.value = secondsToHours(this.newInspection._engineHours).toFixed(1));
        }
        if (addressPopulated && (!needPopulateOtherInfo || (inspectionOdometer.value && inspectionEngineHours.value))) {
            return undefined;
        }

        this.disableUIActions(true);

        return Promise.all([this.getDvirLocation(), needPopulateOtherInfo ? this.getLatestStatusData() : Promise.resolve(null)])
            .then(([address, latestStatusData]) => {
                if (inspectionAddress) {
                    if (address) {
                        this.newAddress = address;
                        if (address.region) {
                            this.newInspection._region = address.region;
                        }
                        if (address.country) {
                            this.newInspection._country = address.country;
                        }
                        inspectionAddress.textContent = this.newAddress.formattedAddress;
                    } else {
                        inspectionAddress.textContent = this.getString("Your current location could not be detected. Please enter your address.");
                    }
                }

                if (inspectionOdometer) {
                    if (latestStatusData?.odometer) {
                        this.newInspection._odometer = latestStatusData.odometer;
                        inspectionOdometer.value = this.app.localizerService.toLocalDistance(latestStatusData.odometer, true).toFixed(1);
                    } else {
                        inspectionOdometer.value = "";
                    }
                }
                if (inspectionEngineHours) {
                    if (latestStatusData?.engineHours) {
                        this.newInspection._engineHours = latestStatusData.engineHours;
                        inspectionEngineHours.value = secondsToHours(latestStatusData.engineHours).toFixed(1);
                    } else {
                        inspectionEngineHours.value = "";
                    }
                }
                this.toggleVisibilityForOtherInfoFields();
                this.renderDone();
                this.disableUIActions(false);
            })
            .catch(e => {
                handleApi(e);
                this.disableUIActions(false);
            });
    }

    populateAssets(assetId: string, model?: DVIRLog) {
        let asset;
        let currentVehicle = getCurrentVehicle();
        if (currentVehicle && currentVehicle.id === assetId) {
            asset = new Device(this.deviceCache.get({ id: assetId })[0]);

            if (model) {
                model.device = asset;
            }
        } else {
            let trailer = this.trailerAttachmentCollection.findTrailer(assetId);
            if (trailer) {
                asset = trailer;

                if (model) {
                    model.trailer = asset;
                }
            }
        }

        return asset;
    }

    populateFromExisting() {
        this.newInspection.dVIRDefects = this.model.getCarryOverDefects();
        this.newInspection.device = this.model.device;
        this.newInspection.trailer = this.model.trailer;

        // MYG-113773: Must reset these async functions to avoid duplications
        this.mediaFileLoadFunctions = [];

        this.newInspection.dVIRDefects.map(dvirDefect => {
            if (dvirDefect.defectRemarks && dvirDefect.defectRemarks.length > 0 && this.showCameraIcons && this.canViewMedia) {
                dvirDefect.defectRemarks.forEach(defectRemark => {
                    if (defectRemark.mediaFiles && defectRemark.mediaFiles.length > 0) {
                        let remarkWithMediaId = defectRemark.id as string;
                        let mediaFileId = defectRemark.mediaFiles[0].id;

                        if (mediaFileId) {
                            this.mediaFileLoadFunctions.push(() => {
                                void getCachedImageForRemark(remarkWithMediaId, mediaFileId, this.app.store, this.translator);
                            });
                        }
                    }
                });
            }
        });
    }

    populateLogDefectList() {
        if (this.model && this.model.defectList && this.defectListsDict.hasOwnProperty(this.model.defectList.id)) {
            this.newInspection.defectList = this.model.defectList;
            this.currentDefectList = this.defectListsDict[this.model.defectList.id];
            return;
        }

        if (this.currentDefectList) {
            this.newInspection.defectList = this.currentDefectList;
        }
    }

    populateDefectLists() {
        return new Promise(resolve => {
            let successCallback = () => {
                if (!this.currentDefectList) {
                    this.currentDefectList = this.defectLists[0];
                }

                this.defectLists.forEach(defectList => {
                    this.defectListsDict[defectList.id] = defectList;
                });
                resolve();
            };

            return getDefectListsForAsset(this.api, this.app.store, this.newInspection.getResource() as IIdEntity, this.newInspection.getAssetType())
                .then(defectLists => {
                    // Archived lists are kept in the cache (for carry-over defect name/severity
                    // resolution) but must not be selectable/bindable for a *new* inspection.
                    this.defectLists = (defectLists || []).filter(defectList => !defectList.isArchived);
                    if (this.defectLists && this.defectLists.length > 0) {
                        return Promise.all(this.defectLists.map(defectList => translateDefectListName(defectList.name, this.translator)));
                    }

                    return Promise.resolve([]);
                })
                .then(translatedNames => {
                    if (!(translatedNames && translatedNames.length > 0)) {
                        successCallback();
                        return;
                    }
                    this.defectLists.forEach((defectList, i) => {
                        defectList.name = htmlEscape(translatedNames[i]);
                    });
                    successCallback();
                });
        });
    }

    resetNewInspectionDefects() {
        if (this.model) {
            this.populateFromExisting();
        } else {
            this.newInspection.dVIRDefects = [];
        }
        this.renderDone();
    }

    displayChecklist() {
        let attachElement = document.getElementById("inspection-list-panel") as HTMLElement;
        this.resetNewInspectionDefects();
        this.inspectionChecklist = this.createChecklist();
        return this.inspectionChecklist.renderList(attachElement, true);
    }

    render() {
        void this.renderImpl();
        return this;
    }

    renderImpl() {
        let model = this.model || this.newInspection;
        let firstDvir = !this.model || !model.id;
        const canEditDvir = this.app.securityService.can("useDvir");
        const canInspectDvir = this.app.securityService.can("inspectDvir") && canEditDvir;
        const canRepairDvir = this.app.securityService.can("repairDvir");
        const canCertifyDvir = this.app.securityService.can("certifyDvir") && canEditDvir;
        let needsInspect = canInspectDvir && (firstDvir || this.model.isComplete(this.app));

        if (!model || !model.isActive()) {
            this.goBack();
            return Promise.resolve();
        }

        let existingIncompleteDvir = needsInspect ? this.getExistingIncompleteDvir() : null;
        if (existingIncompleteDvir) {
            //If the selected resource has an uncertified log in localStorage, don't let them create a new DVIR record
            this.goBack(existingIncompleteDvir.id);
            return Promise.resolve();
        }

        let scrollDiv = document.querySelector("div#scroll-content");
        if (scrollDiv) {
            scrollDiv.scrollTop = 0;
        }

        let populateDefectNamesTask;
        let needsRepair = canEditDvir && !model.isCertified() && model.hasUnrepairedDefects(true);
        if (needsRepair) {
            populateDefectNamesTask = getDefectNames(
                this.translator,
                this.api,
                this.app.store,
                model.getClonedUnrepairedDefects(),
                model.getAssetType(),
                undefined,
                this.populatedDefectNames
            );
        } else {
            populateDefectNamesTask = Promise.resolve({});
        }

        let populateDefectListsTask;
        if (needsInspect) {
            populateDefectListsTask = this.populateDefectLists();
        } else {
            populateDefectListsTask = Promise.resolve([]);
        }

        let populateMissingDefectListTask;
        let unrepairedDefects = this.model ? this.model.getCarryOverDefects() : [];
        if (needsInspect && this.model && !this.model.defectList && unrepairedDefects.length > 0) {
            populateMissingDefectListTask = getDefectListForDefect(this.api, this.app.store, unrepairedDefects[0].defect, this.model.getAssetType());
        } else {
            populateMissingDefectListTask = Promise.resolve(null);
        }

        return Promise.all([populateDefectNamesTask, populateMissingDefectListTask, populateDefectListsTask])
            .then(taskResults => {
                // MYG-135146 carry-over fix: a carry-over defect's original list may now be
                // archived (getDefectListForDefect resolves it for hydration purposes). Don't
                // bind a new inspection to an archived list — keep the non-archived fallback
                // from populateDefectLists() instead. The carry-over defects themselves still
                // display correctly regardless of this binding (see populateFromExisting).
                if (taskResults[1] && !(taskResults[1] as IDefect).isArchived) {
                    this.currentDefectList = taskResults[1];
                }

                needsInspect && this.populateLogDefectList();
                let populatedDefects = this.getRepairHtml(taskResults[0], canRepairDvir);
                let newDvirAsset = this.newInspection && this.newInspection.getResource();
                let assetName = newDvirAsset && newDvirAsset.name || "";
                const isComplete = model && model.isComplete(this.app);
                const hasBlockingUnrepairedDefect = model && model.hasBlockingUnrepairedDefect(this.app.store);
                const needsReview = model && model.needsReview(this.app.store);
                return this.display(this.template({
                    log: this.model,
                    canInspectDvir,
                    needsRepair,
                    assetName,
                    populatedDefects,
                    hasCarryOverDefects: model.hasUnrepairedDefects(true),
                    isComplete,
                    hasBlockingUnrepairedDefect,
                    defectLists: this.defectLists || [],
                    defaultListId: this.currentDefectList && this.currentDefectList.id,
                    defaultListName: this.currentDefectList && this.currentDefectList.name,
                    needsCertify: canCertifyDvir && this.model && needsReview,
                    longDistanceUnits: this.app.localizerService.getLongDistanceUnits(this.isMetric),
                    shortDistanceUnits: this.app.localizerService.getShortDistanceUnits(this.isMetric),
                    isTrailer: model.getResourceType() === "trailer",
                    hasFeatureFlag: this.app.featureFlagService.hasFeatureFlag.bind(this.app.featureFlagService)
                }))
                .then(() => {
                    needsRepair && this.toggleRepairSaveButton();
                    this.viewDvirView && this.viewDvirView.setElement(document.getElementById("view-panel")).render();
                    let repairCheckboxes = Array.from(document.getElementsByTagName("input")).filter(element => element.id.startsWith("repair"));
                    repairCheckboxes.forEach(checkbox => checkbox.setAttribute("aria-expanded", `${ checkbox.checked }`));
                    repairCheckboxes.forEach(checkbox => {
                        checkbox.addEventListener("change", (event) => {
                            const target = event.target as HTMLInputElement;
                            target.setAttribute("aria-expanded", `${ target.checked }`);
                        });
                    });
                    let inspectTask = Promise.resolve();
                    if (canInspectDvir && needsInspect) {
                        this.populateCurrentAddressAndOtherInfo();
                        inspectTask = this.displayChecklist();  // Calls renderDone, so it ends up loading images twice :(
                        this.toggleVisibilityForOtherInfoFields();
                    }

                    return inspectTask;
                })
                .then(() => this.renderDone());
            });
    }

    renderDone() {
        let doneButton = document.querySelector("#new-inspection");

        if (doneButton) {
            if (this.newInspection.hasDefects()) {
                doneButton.className = doneButton.className.replace(/ button_positive/g, "");
                doneButton.innerHTML = `<svg class="svgIcon" aria-hidden="true"><use xlink:href="#drive-ok-circled"></use></svg> ${ this.getString("Submit") }`;
            } else {
                if (doneButton.className.indexOf("button_positive") === -1) {
                    doneButton.className += " button_positive";
                }

                doneButton.innerHTML = `<svg class="svgIcon" aria-hidden="true"><use xlink:href="#drive-ok-circled"></use></svg>
                                        ${ this.getString("Submit (no defects)") }`;
            }
        }
        this.loadAllMediaFiles();
    }

    disableButtons(disable: boolean) {
        let buttons = document.querySelectorAll<HTMLInputElement>("div#scroll-content button:not(.button_simple)");
        for (let i = 0; i < buttons.length; i++) {
            buttons[i].disabled = disable;
        }
    }

    disableButton(id: string, disable = true) {
        let button = document.getElementById(id) as HTMLInputElement;
        if (button) {
            button.disabled = disable;
        }
    }

    removeInspectionList() {
        this.inspectionChecklist && this.inspectionChecklist.remove();
        this.inspectionChecklist = null;
    }

    createChecklist(): InspectionChecklist {
        this.removeInspectionList();
        return new InspectionChecklist({
            translator: this.translator,
            model: this.newInspection,
            onChange: () => this.renderDone(),
            defectList: this.currentDefectList,
            store: this.app.store
        }, this.app.userService, this.app.userHosRuleSetService, this.app.securityService);
    }

    addOnDutyLog (dvirStartTime: Date, comment: string, repairComments: string[] = []): Promise<boolean> {
        if (!this.app.securityService.can("useHos") || this.app.store.getState().data.ELDJurisdiction === "EWD") {
            return Promise.resolve();
        }

        let logCollection = this.app.dutyStatusLogService.getHOSLogs(false);
        let standardStatusLogs = this.app.dutyStatusLogService.getSortedHosLogs(logCollection.filter(hosLog => hosLog.isLogRequiredForDvirOnLog()));
        let latestLog = this.app.dutyStatusLogService.getLatestLog(standardStatusLogs);
        let dvirLogAnnotations: string[] = ["Asset Inspection: Post-trip", "Asset Inspection: In-trip", "Asset Inspection: Pre-trip", "Asset Inspection: Repair made", "Asset Inspection: Certified"];

        return this.app.translationService.getTranslator().translate(dvirLogAnnotations).then(translatedAnnotations => {
            let commentForOnLog = translatedAnnotations[dvirLogAnnotations.indexOf(this.app.hosUtils.getAnnotationForOnLog(comment))];
            if (this.app.hosUtils.shouldCreateOnLog(latestLog, standardStatusLogs)) {
                const additionalLogs = this.app.dutyStatusLogService.getAdditionalLogsBeforeInspectionOnLogIfDrivingInPc(dvirStartTime, this.currentUser as IUser, latestLog);
                return Promise.all(additionalLogs.map(log => this.sync.dutyStatusLogs.addItem(log, true)))
                    .then(() => this.addDvirOnLog(dvirStartTime, commentForOnLog, repairComments));
            } else if (latestLog && latestLog.status === "PC_Exempted") {
                return Promise.resolve(false);
            } else if (latestLog) {
                let date = getServerDate();
                let localizedDate = dayjs(dstCalculator.toLocalDateTime(date)).format("lll");
                let currentUser = this.app.userService.getCurrentUser();
                let driver: IIdEntity | "NoUserId" = currentUser ? { id: currentUser.id } : "NoUserId";
                latestLog.addAnnotation({
                    id: idSerializer.get(),
                    driver,
                    comment: this.app.hosUtils.getCommentWithDatetime(commentForOnLog, localizedDate, repairComments),
                    dateTime: date
                });
                this.sync.dutyStatusLogs.setItem(latestLog, true);
            }
            return Promise.resolve(false);
        });
    }

    addDvirOnLog(dvirStartTime: Date, commentForOnLog: string, repairComments: string[] = []): Promise<boolean> {
        let currentUser = this.app.userService.getCurrentUser();
        let currentVehicle = getCurrentVehicle() as IIdEntity;
        let driver: IIdEntity = { id: currentUser ? currentUser.id : "NoUserId" };
        let device = { id: currentVehicle.id };
        let newLog = new DutyStatusLog({
            id: idSerializer.get(),
            dateTime: dvirStartTime.toISOString(),
            status: "ON",
            device,
            driver,
            coDrivers: this.app.userService.getCoDrivers(currentUser ? currentUser.name : undefined),
            origin: "Manual"
        });


        let date = getServerDate();
        let localizedDate = dayjs(dstCalculator.toLocalDateTime(date)).format("lll");

        newLog.addAnnotation({
            id: idSerializer.get(),
            driver,
            comment: this.app.hosUtils.getCommentWithDatetime(commentForOnLog, localizedDate, repairComments),
            dateTime: date
        });
        return this.sync.dutyStatusLogs.addItem(newLog, true).then(() => Promise.resolve(true));
    }

    getUpdatedDefects(): [boolean, IDvirDefects[]] {
        const unmodifiedLog = this.app.dvirLogsService.getDvirLogById(this.model.id);
        const originalDvirDefects = unmodifiedLog?.dVIRDefects ?? [];
        let newDefects = this.model.getClonedAllDefects();
        let now = getServerDate().toISOString();
        let repairUser = { "id": this.currentUser?.id || "NoUserId"};
        let isChanged = false;
        let needsHosSync = false;

        newDefects.forEach(defect => {
            let remarkHtml = document.querySelector(`input.newRemark[data-id="${ defect.id }"]`) as HTMLInputElement;
            if (remarkHtml && remarkHtml.value !== "") {
                // Add new remark
                let remarks: IDefectRemark[] = defect.defectRemarks || [];
                remarks.push({
                    remark: remarkHtml.value,
                    dateTime: now,
                    user: repairUser
                });

                defect.defectRemarks = remarks;
                isChanged = true;
            }

            if (defect.repairStatus !== "NotRepaired") {
                const originalDefect = originalDvirDefects.find(d => d.id === defect.id);
                const changedRepairStatus = originalDefect && originalDefect?.repairStatus !== defect.repairStatus;
                // ESUPSD-53433: the stored copy is not always available to compare against - the log
                // may have been created offline and not persisted yet, or sync may have rewritten the
                // defect id. A repaired defect must carry repair info regardless, otherwise the server
                // rejects the push and the queued inspection is lost. Only "Repaired" qualifies:
                // "NotNecessary" means no repair took place, so it must stay free of repair info.
                const needsRepairInfo = defect.repairStatus === "Repaired" && !defect.repairDateTime;
                if (changedRepairStatus || needsRepairInfo) {
                    defect.repairDateTime = now;
                    defect.repairUser = repairUser;
                    isChanged = true;
                    needsHosSync = true;
                }
            }
        });

        if (!isChanged) {
            throw new Error("Nothing was changed!");
        }

        return [needsHosSync, newDefects];
    }

    typeRepairRemark(e) {
        this.toggleRepairSaveButton((e?.target?.value || "").trim());
    }

    toggleRepairSaveButton(needsSave?: boolean) {
        let repairButton = document.querySelector("button#save-repair") as HTMLInputElement;

        if (repairButton) {
            if (!needsSave) {
                // Verify if anything else on the page has remaining changes
                let actionableDefectOptions: NodeListOf<HTMLInputElement> = document.querySelectorAll(".repair-input-container input");
                if (!Array.prototype.slice.call(actionableDefectOptions)
                    .find(defectOption => (defectOption.type === "radio" && defectOption.checked) || (defectOption.type === "text" && (defectOption.value || "").trim()))) {
                    if (!repairButton.disabled) {
                        repairButton.disabled = true;
                        let innerString = this.getString("No changes");
                        repairButton.innerHTML = `<svg class="svgIcon bigger bottom-margin" role="img" aria-label="No changes icon"><use xlink:href="#drive-wrench"></use></svg> ${ innerString }`;
                        repairButton.classList.add("button_disabled");
                    }
                    return;
                }
            }

            if (repairButton.disabled) {
                repairButton.disabled = false;
                let innerString = this.getString("Save");
                repairButton.innerHTML = `<svg class="svgIcon bigger bottom-margin" role="img" aria-label="Save icon"><use xlink:href="#drive-wrench"></use></svg> ${ innerString }`;
                repairButton.classList.remove("button_disabled");
            }
        }
    }

    removeImage(parentElement: HTMLElement | null, cameraIconDivElement: HTMLElement | null): void {
        if (parentElement && cameraIconDivElement) {
            parentElement.removeChild(cameraIconDivElement);
            let filePath = cameraIconDivElement.getAttribute("filePath");
            if (filePath) {
                void deleteImage(this.app.store, filePath);
            }
        }
    }

    repairAndCertify(e) {
        let saveButton = <HTMLInputElement>document.querySelector("#save-repair");
        if ((saveButton && !saveButton.disabled)) {
            void this.repair()
            .then(() => this.certify(e));
        } else {
            this.certify(e);
        }
    }

    repair() {
        this.selectiveSync.add(SyncType.DVIR);

        let toggleButtons = (hideBtn = true) => {
            let saveButton = <HTMLInputElement>document.querySelector("#save-repair");
            if (saveButton) {
                saveButton.textContent = this.getString(hideBtn ? "Saving in progress" : "Save");
            }
            this.disableButtons(hideBtn);
        };

        this.model.validateResource();

        let updatedDefectsInfo: [boolean, IDvirDefects[]];
        try {
            updatedDefectsInfo = this.getUpdatedDefects();
        } catch (e) {
            return Promise.resolve();
        }

        toggleButtons();
        let dvirRepairComments: string[] = this.getDvirRepairCommentForOnLog(updatedDefectsInfo[1]);
        return (!this.canViewMedia ? Promise.resolve() : this.app.sync.synchronize(undefined, undefined, [SyncType.Media]))
            .then(() => (updatedDefectsInfo[0]
                ? this.addOnDutyLog(this.dvirStartTime, "Asset Inspection: Repair made", dvirRepairComments)
                : Promise.resolve(false)))
            .then(needHosSync => {
                this.showOnDutyToastMsg(needHosSync);
                this.model.dVIRDefects = updatedDefectsInfo[1];

                if (needHosSync) {
                    this.selectiveSync.add(SyncType.HOS);
                }
                return this.app.sync.dvirs.setItem(this.model.toJSON(), true);
            })
            .then(() => {
                toastr.success(this.getString("Your changes have been saved."), undefined, undefined, { preventDuplicates: true });
                toggleButtons(false);

                // Update log in case something changed in the sync
                this.fetchAndPopulate(this.model);

                if (this.model.isComplete(this.app)) {
                    // Need to repopulate defects on newInspecton
                    this.createNewDvir();
                    this.updatePageStateBasedOnVehicle();
                    this.populateFromExisting();
                }

                this.viewDvirView && this.viewDvirView.closeExpanded();
                return this.renderImpl();
            })
            .catch(error => {
                if (!this.handleRemarkLengthError(error)) {
                    error && handleApi(error);
                }
                toggleButtons(false);
            });
    }

    certify(e) {

        this.selectiveSync.add(SyncType.DVIR);

        let text = e.currentTarget.innerHTML;
        let toggleButtons = (hideBtn = true) => {
            this.disableButtons(hideBtn);
            e.currentTarget.innerHTML = hideBtn ? this.getString("Saving in progress") : text;
        };

        this.model.validateResource();
        toggleButtons();

        this.addOnDutyLog(this.dvirStartTime, "Asset Inspection: Certified")
            .then(needHosSync => {
                this.showOnDutyToastMsg(needHosSync);
                let certifyElement = document.querySelector("#certifyRemark") as HTMLInputElement;
                let certifyRemark = certifyElement ? certifyElement.value : "";
                let certifiedBy = {
                    "id": this.currentUser?.id || "NoUserId",
                    "name": this.currentUser?.name || "" };
                let certifyDate = getServerDate();

                this.fetchAndPopulate(this.model);
                this.model.certifyRemark = certifyRemark;
                this.model.isSafeToOperate = true;
                this.model.certifiedBy = new User(certifiedBy);
                this.model.certifyDate = certifyDate.toISOString();
                this.viewDvirView && this.viewDvirView.closeExpanded();

                if (this.userInputCoordinates.location) {
                    this.model.location = this.userInputCoordinates.location;
                    this.userInputCoordinates = {};
                }

                if (needHosSync) {
                    this.selectiveSync.add(SyncType.HOS);
                }
                return this.sync.dvirs.setItem(this.model.toJSON(), true);
            })
            .then(() => {
                toggleButtons(false);

                // Update log in case something changed in the sync
                this.fetchAndPopulate(this.model);
                this.createNewDvir();
                this.populateFromExisting();
                return this.renderImpl();
            })
            .catch(error => {
                if (!this.handleRemarkLengthError(error)) {
                    error && handleApi(error);
                }
                toggleButtons(false);
            });
    }

    updateInspectionList(event: Event) {
        let selectElement = event.currentTarget as HTMLSelectElement;
        let defectListId = selectElement.value;
        if (this.defectListsDict.hasOwnProperty(defectListId)) {
            this.currentDefectList = this.defectListsDict[defectListId];
            this.newInspection.defectList = this.currentDefectList;
            selectElement.disabled = true;
            this.displayChecklist().then(() => {
                selectElement.disabled = false;
            });
        }
    }

    getDvirRepairCommentForOnLog(repairedDefects: IDvirDefects[]): string[] {
        if (!repairedDefects) {
            return [];
        }
        let repairedDefectList: string[] = [];
        for (let key in this.populatedDefectNames) {
            if (repairedDefects.some(e => e.defect.id === key && e.repairStatus === "Repaired")) {
                repairedDefectList.push(this.populatedDefectNames[key]);
            }
        }
        return repairedDefectList;
    }

    fillInOtherInformation() {
        let odometerInput = document.getElementById("odometerInput") as HTMLInputElement;
        let engineHoursInput = document.getElementById("engineHoursInput") as HTMLInputElement;

        const parsedOdometerValue = odometerInput && parseFloat(odometerInput.value);
        this.newInspection.odometer = parsedOdometerValue ? this.app.localizerService.toMetresFromLocalDistance(parsedOdometerValue) :
            this.newInspection._odometer || this.newInspection.odometer;
        const parsedEngineHoursValue = engineHoursInput && parseFloat(engineHoursInput.value);
        this.newInspection.engineHours = parsedEngineHoursValue ? hoursToSeconds(parsedEngineHoursValue) : this.newInspection._engineHours ?? undefined;
    }

    fillInCanadianDvirInfo() {
        let hubometerInput = document.getElementById("trailerHubometerInput") as HTMLInputElement;
        let loadWidthInput = document.getElementById("loadWidthInput") as HTMLInputElement;
        let loadHeightInput = document.getElementById("loadHeightInput") as HTMLInputElement;

        let canadianFields = this.app.localizerService.convertStringValuesToNumericMetricForCanadianFields(
            hubometerInput ? hubometerInput.value : "",
            loadWidthInput ? loadWidthInput.value : "",
            loadHeightInput ? loadHeightInput.value : "",
            this.isMetric
        );
        canadianFields.hubometer && canadianFields.hubometer > 0 && (this.newInspection.odometer = canadianFields.hubometer);
        canadianFields.loadWidth && canadianFields.loadWidth > 0 && (this.newInspection.loadWidth = canadianFields.loadWidth);
        canadianFields.loadHeight && canadianFields.loadHeight > 0 && (this.newInspection.loadHeight = canadianFields.loadHeight);
    }

    handleRemarkLengthError(error: unknown): boolean {
        if (typeof error !== "object" || error === null || !("message" in error)) { return false; }
        const msg = (error as { message?: unknown }).message;
        if (typeof msg !== "string") { return false; }
        const key = InspectDvirView.remarkLengthErrorKeys[msg];
        const translated = key ? this.getString(key) : undefined;
        if (translated) {
            toastr.warning(translated, undefined, undefined, { positionClass: "toast-bottom-center" });
            return true;
        }
        return false;
    }

    createNewInspection() {
        let hasErrors = false;
        this.newInspection.validateResource();

        let inspectionTypeWarning = document.getElementById("pre-post-trip-required-warning") as HTMLElement;
        inspectionTypeWarning && hide(inspectionTypeWarning);

        let driverInput = document.getElementById("driverRemark") as HTMLInputElement;
        let driverRemark = driverInput ? driverInput.value : "";
        let inspectionType = document.querySelector("input[name=\"new-inspect-dvir-type\"]:checked") as HTMLInputElement;
        if (!inspectionType && inspectionTypeWarning) {
            inspectionTypeWarning.style.display = "flex";
            (document.getElementById("new-inspection-type-panel") as HTMLElement).scrollIntoView(true);
            hasErrors = true;
        }

        if (this.inspectionChecklist?.validateRequiredParts()) {
            toastr.warning(
                this.getString("You must select a defect or check off 'No defect' if there are no defects for mandatory items."),
                undefined,
                undefined,
                { positionClass: "toast-bottom-center" }
            );
            hasErrors = true;
        }

        let now = getServerDate();
        let defectsWithRemarks: IDvirDefects[] = [];

        try {
            defectsWithRemarks = (this.inspectionChecklist as InspectionChecklist).saveRemarks(now.toISOString());
        } catch (e) {
            hasErrors = true;
        }

        if (hasErrors) {
            let firstError = document.querySelector(`.noticeBox--type-error:not([style*="display: none"])`) as HTMLElement;
            if (firstError) {
                firstError.scrollIntoView();
                firstError.focus();
            }
            return;
        }

        // MYG-144456: the unresolved-defects panel toggles repairStatus in place on the previous
        // log's defects, and populateFromExisting() puts those same objects on the new
        // inspection. A driver who marks a carry-over defect "Resolved" without pressing Save
        // therefore submits it as "Repaired" with no repair info, and the server rejects the whole
        // Add<DVIRLog> call - the inspection is lost while its ON-duty annotation is already
        // written. Stamp what the Save/repair flow would have stamped so the resolution is kept.
        let carryOverRepairs = this.stampMissingRepairInfo(defectsWithRemarks, now.toISOString());
        // MYG-146685: resolving a carry-over defect on submit is a repair, so it must be traceable
        // in the driver's HOS log exactly as the Save/repair flow's repair is.
        let carryOverRepairComments = this.getDvirRepairCommentForOnLog(carryOverRepairs);

        let cancelDvir = false;
        let addDefectIds = (defects: IDvirDefects[]) => {
            defects.forEach(defect => {
                if (!defect.id) {
                    defect.id = idSerializer.get();
                }
            });
        };

        let syncProgressCallback = (_type: string, _itemsSynced: number, _itemsToSync: number, errorObject?: Error | IApiError) => {
            let errorMessage = errorObject && errorObject.message ? errorObject.message : "";
            if (errorMessage.indexOf("violates foreign key constraint") > -1 && errorMessage.indexOf("fk_dvirlog_defect") > -1) {
                cancelDvir = true;
            }
        };

        let isCanadianDvir = this.isCanada !== undefined ? this.isCanada : (this.newInspection && this.newInspection._country === "Canada");
        isCanadianDvir && this.fillInCanadianDvirInfo();
        this.fillInOtherInformation();

        formInspectedVehicleString(this.translator, this.newInspection, this.currentDefectList, this.app.store, undefined, this.isAustralia)
            .then(translatedVehicleString => {
                this.inspectionDialog = new Dialog(
                    "sign-dvir",
                    `<span class="noTranslate">${ translatedVehicleString }</span>`,
                    this.translator,
                    "Sign Asset Inspection",
                    [[
                        {
                            text: "No",
                            id: "sign-dvir-no",
                            className: "button_secondary",
                            callback: () => {
                                this.inspectionDialog.remove();
                            }
                        }, {
                            text: "Yes",
                            id: "sign-dvir-yes",
                            // todo: set openCabWorkflow to false somewhere in this callback
                            callback: () => {
                                const dvirEndTime = getServerDate();
                                let newOnLogCreated = false;
                                let newDvirId: string;

                                this.selectiveSync.add(SyncType.DVIR);

                                const signDvirLogTimer = timer(Source.USER, "Sign DVIR tasks", this.currentUser);
                                (!this.canViewMedia ? Promise.resolve() : this.app.sync.synchronize(undefined, undefined, [SyncType.Media]))
                                .then(() => this.addOnDutyLog(this.dvirStartTime, inspectionType.value))
                                .then(inspectionLogCreated => (carryOverRepairs.length === 0
                                    ? Promise.resolve(inspectionLogCreated)
                                    : this.addOnDutyLog(this.dvirStartTime, "Asset Inspection: Repair made", carryOverRepairComments)
                                        .then(repairLogCreated => inspectionLogCreated || repairLogCreated)))
                                .then(needHosSync => {
                                    newOnLogCreated = needHosSync;
                                    addDefectIds(defectsWithRemarks);
                                    this.newInspection.driverRemark = driverRemark;
                                    this.newInspection.dateTime = getServerDate().toISOString();
                                    this.newInspection.logType = inspectionType.value as TLogType;
                                    this.newInspection.dVIRDefects = defectsWithRemarks;
                                    let durationInMilliseconds = 0;
                                    if (dvirEndTime) {
                                        durationInMilliseconds = dvirEndTime.getTime() - this.dvirDurationStartTime.getTime();
                                        this.newInspection.duration = msToTimespan(durationInMilliseconds);
                                    }
                                    // Checking if 'dvirEndTime' is valid because we found entry of dvirLogs in bigQuery with duration = null
                                    // It is only possible if dvirEndTime is undefined/null. Although it is very unlikely
                                    // We want to add this log for more information
                                    if (!dvirEndTime || durationInMilliseconds <= 0 || !this.newInspection.duration) {
                                        LogError(
                                            Source.ERROR,
                                            `Invalid DVIR duration: ${ this.newInspection.duration }. StartTime: ${ this.dvirDurationStartTime.toISOString() }, EndTime: ${ dvirEndTime ? dvirEndTime.toISOString() : undefined }`,
                                            this.currentUser ? this.currentUser.id : undefined
                                        );
                                    }

                                    if (this.newAddress && this.newAddress.location) {
                                        this.newInspection.location = this.newAddress.location;
                                    }

                                    if (needHosSync) {
                                        this.selectiveSync.add(SyncType.HOS);
                                    }
                                    this.app.store.dispatch(MobileActions.setDvirDurationStartTime(undefined));
                                    return this.sync.dvirs.addItem(this.newInspection.toJSON(), true);
                                })
                                .then(newDvir => {
                                    newDvirId = newDvir.id;
                                    this.completedDvir = true;
                                    if (newDvir.trailer.id === "NoTrailerId" && newDvir.device.id && newDvir.logType === ("PreTrip" as TLogType)) {
                                        this.app.store.dispatch(OpenCabWorkflowActions.setPreTripDvirCompleteVehicleId(newDvir.device.id));
                                    }
                                    // location for asset inspection is altered, need to sync immediately
                                    if (this.userInputCoordinates.location) {
                                        let syncTypeArray = [SyncType.DVIR];
                                        return this.sync.synchronize(false, syncProgressCallback, syncTypeArray);
                                    }

                                    return Promise.resolve();
                                })
                                .then(() => {
                                    if (!this.userInputCoordinates.location) {
                                        return Promise.resolve();
                                    }

                                    // location for asset inspection is altered
                                    this.newInspection.id = newDvirId;
                                    this.fetchAndPopulate(this.newInspection);
                                    this.newInspection.location = this.userInputCoordinates.location;

                                    this.userInputCoordinates = {};

                                    return this.sync.dvirs.setItem(this.newInspection.toJSON(), true);
                                })
                                .then(() => {
                                    if (cancelDvir) {
                                        this.showOnDutyToastMsg(newOnLogCreated);
                                        this.render();
                                        void this.populateDefectLists();
                                        return Promise.reject();
                                    } else {
                                        this.inspectionDialog.remove();
                                        let toastSuccessMessage = `${ this.getString("Inspection complete") }`;
                                        if (this.newInspection.duration) {
                                            toastSuccessMessage = newOnLogCreated
                                            ? this.getString("Inspection done in {0}. You're {1} as of {2}.")
                                            .replace("{1}", "<b>" + this.getString("On Duty") + "</b>").replace("{2}", dayjs(dstCalculator.toLocalDateTime(this.dvirStartTime)).format("LT"))
                                            : this.getString("Inspection done in {0}.");

                                            toastSuccessMessage = toastSuccessMessage.replace("{0}", getDisplayTimeSpan(
                                                this.newInspection.duration,
                                                true,
                                                this.getString("d"),
                                                this.getString("h"),
                                                this.getString("{minutes}m"),
                                                this.getString("s")
                                            ));
                                        }
                                        toastr.success(toastSuccessMessage, undefined, undefined, {allowHtml: true});

                                        this.goBack(newDvirId);
                                        return Promise.resolve();
                                    }
                                })
                                .then(() => {
                                    signDvirLogTimer(true);
                                })
                                .catch(e => {
                                    signDvirLogTimer(false);
                                    this.inspectionDialog.remove();
                                    if (!this.handleRemarkLengthError(e)) {
                                        e && handleApi(e);
                                    }
                                });
                            },
                            spinner: true
                        }]
                    ],
                    {
                        closable: false,
                        isNightMode: this.app.store.getState().data.nightMode
                    }
                );
                void this.inspectionDialog.open();
            }).catch(error => {
                if (!this.handleRemarkLengthError(error)) {
                    error && handleApi(error);
                }
            });
    }

    addressInput() {
        let showAddressErrorDialog = () => {
            let addressErrorDialog = new Dialog(
                "address-error",
                "The address you entered could not be found. Please re-enter it and try again.",
                this.translator,
                "Unknown address",
                undefined,
                {
                    isNightMode: this.app.store.getState().data.nightMode
                }
            );
            void addressErrorDialog.open();
        };

        this.addressDialog = new Dialog(
            "dvir-address-input",
            this.logAddressInputTemplate({ isAustralia: this.isAustralia }),
            this.translator,
            "Enter an address",
            [{
                id: "dvirAddressCancel",
                className: "button_secondary",
                text: "Cancel",
                callback: () => {
                    this.addressDialog.remove();
                }
            }, {
                id: "dvirAddressOK",
                text: "OK",
                spinner: true,
                callback: () => {
                    let thisDialog = document.getElementById("dvir-address-input");
                    if (!thisDialog) {
                        this.addressDialog.remove();
                        return;
                    }

                    let street = (<HTMLInputElement>thisDialog.querySelector("#tempaddress-street")).value.trim();
                    let city = (<HTMLInputElement>thisDialog.querySelector("#tempaddress-city")).value.trim();
                    let stateProvince = (<HTMLInputElement>thisDialog.querySelector("#tempaddress-state")).value.trim();
                    let addressCountry = this.isAustralia ? "Australia" : (<HTMLInputElement>thisDialog.querySelector("#tempaddress-country")).value.trim();
                    let formattedAddress = `${ (street ? `${ street }, ` : "") }${ (city ? `${ city }, ` : "") }${ (stateProvince ? `${ stateProvince }, ` : "") }${ (addressCountry || "") }`;
                    this.newInspection._region = stateProvince;
                    this.newInspection._country = addressCountry.toLowerCase() === "ca" ? "Canada" : addressCountry;
                    this.renderDone();
                    this.toggleVisibilityForOtherInfoFields();
                    let handleSuccess = () => {
                        let locationElement = document.getElementById("inspection-address");
                        if (locationElement) {
                            locationElement.textContent = formattedAddress;
                        }
                        this.addressDialog.remove();
                    };
                    let handleError = () => {
                        this.addressDialog.remove();
                        showAddressErrorDialog();
                    };

                    return geocodeAddresses(this.api, [formattedAddress])
                        .then(addressArray => {
                            if (addressArray[0] !== null && !(addressArray[0].x === 0 && addressArray[0].y === 0)) {
                                this.userInputCoordinates.location = {
                                    location: {
                                        x: addressArray[0].x,
                                        y: addressArray[0].y
                                    }
                                };

                                // Delete address so the correctly formatted one can be looked up later
                                // We might have set _isManualAddress, so remove that too
                                delete this.newInspection._address;
                                delete this.newInspection._region;
                                delete this.newInspection._country;
                                delete this.newInspection._isManualAddress;
                                void populateAddressesRedux(this.api, this.app.store, [{ id: this.newInspection.id, coordinate: this.userInputCoordinates.location.location }], true)
                                    .then(([address]) => {
                                        this.newInspection._region = address && address.region || undefined;
                                        this.newInspection._country = address && address.country || undefined;
                                        this.renderDone();
                                        this.toggleVisibilityForOtherInfoFields();
                                        handleSuccess();
                                    });
                            } else {
                                // Address might not exist. Show error
                                handleError();
                            }
                        })
                        .catch(error => {
                            // Only condition we keep the address is if network error, because we can try later in the sync. Otherwise, show error
                            if (isNetworkError(error)) {
                                this.newInspection._address = formattedAddress;
                                this.newInspection._isManualAddress = true;
                                handleSuccess();
                            } else {
                                handleApi(error);
                                handleError();
                            }
                        });
                }
            }],
            {
                closable: false,
                isNightMode: this.app.store.getState().data.nightMode
            }
        );
        void this.addressDialog.open();
    }

    createAudit() {
        let model = this.model || this.newInspection;
        let asset = model && model.getResource();

        if (this.completedDvir || !asset || !asset.id || asset.id === "NoDeviceId" || asset.id === "NoTrailerId") {
            // only create skip audit if DVIR was not finished and asset exists
            return;
        }

        // May need to populate asset
        if (!asset.name) {
            asset = this.populateAssets(asset.id) || asset;
        }

        let optionalParams;
        optionalParams = {
            assetType: model.getResourceType(),
            assetId: asset.id,
            assetName: asset.name || ""
        };

        auditSkip(this.app.api, AuditType.DvirInspect, this.isDetach ? "Detach trailer" : "Attach trailer", optionalParams);
    }

    goBack(newDvirId?: string, navigateToAssets?: boolean) {
        this.completedDvir = true;
        if (this.workflow) {
            dispatcher.trigger(DispatcherEvents.CompleteWorkflowStep, newDvirId, navigateToAssets);
        } else {
            if (navigateToAssets) {
                this.app.store.dispatch(MobileActions.setPreviousPage("dvir/main"));
            }
            this.app.router.navigate(this.isTrailerWorkflow || navigateToAssets ? "assets/main" : "dvir/main", true);
        }
    }

    remove() {
        if (this.shouldDoAuditOnRemove) {
            if (this.isTrailerWorkflow) {
                this.createAudit();
            }

            if (this.isDetach) {
                let trailerAttachment = this.trailerAttachmentCollection.get(this.trailerAttachment);
                if (trailerAttachment) {
                    trailerAttachment.detach();
                    this.sync.trailerAttachments.setItem(trailerAttachment).then(() => this.trigger("trailerRemoved")).catch(handleApi);
                }
            }
        }

        this.viewDvirView && this.viewDvirView.remove();
        this.viewDvirView = null;
        this.removeInspectionList();

        this.addressDialog && this.addressDialog.remove();
        this.inspectionDialog && this.inspectionDialog.remove();
        dispatcher.trigger(DispatcherEvents.HideTopbarMessage);

        // Sync HOS/DVIR on leaving inspect page if not workflow
        if (!this.workflow && this.selectiveSync.size > 0) {
            this.sync.synchronize(false, null, Array.from(this.selectiveSync));
        }

        this.app.store.dispatch(MobileActions.setDvirDurationStartTime(undefined));
        super.remove();
        return this;
    }

    checkShowCameraIcons(): boolean {
        return window.driveApp?.debug?.base64TestImage || isCameraPluginPresent();
    }

    private toggleVisibilityForOtherInfoFields() {
        let isCanadianDvir = this.isCanada !== undefined ? this.isCanada : (this.newInspection && this.newInspection._country === "Canada");

        let otherInfoPanel = document.getElementById("other-info-panel");
        if (otherInfoPanel) {
            const canadaInfoPanel = document.getElementById("canada-info-panel");
            canadaInfoPanel && (isCanadianDvir ? show(canadaInfoPanel) : hide(canadaInfoPanel));
            const shouldShowOtherInfo = (this.model || this.newInspection).getResourceType() !== "trailer" || isCanadianDvir;
            shouldShowOtherInfo ? show(otherInfoPanel) : hide(otherInfoPanel);
        }
    }

    private getCheckedHtml(expectedRepairStatus: TRepairStatus, actualRepairStatus: TRepairStatus | undefined) {
        return expectedRepairStatus === actualRepairStatus ? ` checked="checked"` : "";
    }

    private getRepairHtml(defectsDict: { [id: string]: IDvirDefects[] }, canRepair: boolean) {
        if (!Object.keys(defectsDict).length) {
            return [];
        }
        let defaultChecked = this.model.getUnrepairedDefects(true).length <= MAX_DEFECTS_OPEN_BY_DEFAULT;

        // MYG-113773: Must reset these async functions to avoid duplications
        this.mediaFileLoadFunctions = [];

        return Object.keys(defectsDict).map(defectParentId => {
            let defects = defectsDict[defectParentId];

            let defectsHtml = defects.map(dvirDefect => {
                let mediaFile;
                let remarkWithMediaId;
                let startIndex = 0;
                if (dvirDefect.defectRemarks && dvirDefect.defectRemarks.length > 0) {
                    dvirDefect.defectRemarks.forEach(remark => {
                        remark.remark = htmlEscape(remark.remark);
                        if (this.showCameraIcons && this.canViewMedia && remark.mediaFiles && remark.mediaFiles.length > 0) {
                            mediaFile = remark.mediaFiles;
                            remarkWithMediaId = remark.id;
                            startIndex = 1;
                            let mediaFileId = remark.mediaFiles[0].id;
                            if (mediaFileId) {
                                this.mediaFileLoadFunctions.push(() => {
                                    void getCachedImageForRemark(remarkWithMediaId, mediaFileId, this.app.store, this.translator);
                                });
                            }
                        }
                    });

                    dvirDefect.defectRemarks.sort(dateTimeComparator());
                }

                let severity = (dvirDefect.defect as IDefect).severity;
                let defectName = (dvirDefect.defect as IDefect).name;
                if (!(dvirDefect.defect as IDefect).parent && !defectName) {
                    // MYG-135056: only fall back to "Other" when the defect has neither
                    // a parent nor a name. A parentless defect with a real name (e.g.
                    // "Slow to release" from a flat defect list) must keep that name.
                    defectName = this.getString("Other");
                }

                let sharedClass = "repair-input-container repair-checkbox";
                let customCheckboxProps = `data-id="${ dvirDefect.id }"`;
                let getDataStatusProp = (status: string) => ` data-status="${ status }"`;
                let unrepairedCheckbox = renderCheckbox({
                    customClass: "repair-checkbox unrepaired",
                    title: "Mark repair status",
                    labelText: REPAIR_STATUS_STRINGS["NotRepaired"],
                    id: `unrepaired-${ dvirDefect.id }`,
                    checkboxCustomAttributes: customCheckboxProps + getDataStatusProp("NotRepaired") + this.getCheckedHtml("NotRepaired", dvirDefect.repairStatus),
                    type: "radio",
                    inputName: `repair-${ dvirDefect.id }`,
                    isRound: true
                });
                let repairCheckbox = renderCheckbox({
                    customClass: `${ sharedClass } repair`,
                    title: "Mark repair status",
                    labelText: REPAIR_STATUS_STRINGS["Repaired"],
                    id: `repaired-${ dvirDefect.id }`,
                    checkboxCustomAttributes: customCheckboxProps + getDataStatusProp("Repaired"),
                    type: "radio",
                    inputName: `repair-${ dvirDefect.id }`,
                    isRound: true
                });
                let notNecessaryCheckbox = renderCheckbox({
                    customClass: `${ sharedClass } not-necessary`,
                    title: "Mark repair status",
                    labelText: REPAIR_STATUS_STRINGS["NotNecessary"],
                    id: `notNecessary-${ dvirDefect.id }`,
                    checkboxCustomAttributes: customCheckboxProps + getDataStatusProp("NotNecessary") + this.getCheckedHtml("NotNecessary", dvirDefect.repairStatus),
                    type: "radio",
                    inputName: `repair-${ dvirDefect.id }`,
                    isRound: true
                });

                let remarkAriaLabel = this.getString("Add a remark");
                return this.repairDefectTemplate({
                    canRepair,
                    dvirDefect,
                    defectName,
                    criticalLabel: getLabel(severity),
                    severity: (severity || "Normal").toLowerCase(),
                    mediaFile: !!mediaFile,
                    remarkWithMediaId: `remarkWithMediaId_${ remarkWithMediaId }`,
                    startIndex: startIndex,
                    unrepairedCheckbox: unrepairedCheckbox.outerHTML,
                    repairCheckbox: repairCheckbox.outerHTML,
                    notNecessaryCheckbox: notNecessaryCheckbox.outerHTML,
                    remarkAriaLabel: remarkAriaLabel,
                    placeholderText: this.getString("Add a remark")
                });
            });

            let parentDefect = (defects[0].defect as IDefect).parent || (defects[0].defect as IDefect);
            if (!parentDefect.name) {
                parentDefect.name = this.getString("Unknown");
            }
            let defectLabel = parentDefect.name;
            return this.repairPartTemplate({
                defect: parentDefect,
                defectLabel,
                defaultChecked,
                defectCountString: this.getString(defects.length === 1 ? "{0} defect" : "{0} defects").replace("{0}", `${ defects.length }`),
                nestedDefects: defectsHtml
            });
        });
    }

    private loadAllMediaFiles() {
        for (let i = 0; i < this.mediaFileLoadFunctions.length; i++) {
            this.mediaFileLoadFunctions[i]();
        }
    }

    /**
     * Fills in the repair info the server requires on any defect already marked "Repaired" that is
     * missing it, and keeps a repair remark typed next to it in the unresolved-defects panel.
     * Mirrors the stamping getUpdatedDefects() does for the Save/repair flow: only "Repaired"
     * qualifies - "NotNecessary" means no repair took place, so it must stay free of repair info,
     * which the server rejects on it.
     *
     * Returns the defects this call actually repaired - the ones that arrived marked "Repaired"
     * with no repair info, i.e. resolved in the unresolved-defects panel without pressing Save.
     * A defect the Save/repair flow already stamped is left out: its "Repair made" annotation was
     * written then (MYG-146685).
     */
    private stampMissingRepairInfo(defects: IDvirDefects[], repairDateTime: string): IDvirDefects[] {
        let newlyRepaired: IDvirDefects[] = [];
        defects.forEach(defect => {
            if (defect.repairStatus !== "Repaired") {
                return;
            }

            if (!defect.repairDateTime) {
                newlyRepaired.push(defect);
            }

            let repairUser = { "id": this.currentUser?.id || "NoUserId" };
            let remarkHtml = document.querySelector(`input.newRemark[data-id="${ defect.id }"]`) as HTMLInputElement;
            if (remarkHtml && remarkHtml.value !== "") {
                let remarks: IDefectRemark[] = defect.defectRemarks || [];
                // A failed submit leaves the remark on newInspection.dVIRDefects and the input
                // still populated, so a retry would otherwise stamp a second copy.
                if (!remarks.some(existing => existing.remark === remarkHtml.value)) {
                    remarks.push({
                        remark: remarkHtml.value,
                        dateTime: repairDateTime,
                        user: repairUser
                    });

                    defect.defectRemarks = remarks;
                }
            }

            if (!defect.repairDateTime) {
                defect.repairDateTime = repairDateTime;
            }
            if (!defect.repairUser) {
                defect.repairUser = repairUser;
            }
        });

        return newlyRepaired;
    }

    private updateRepairStatus(e: Event) {
        let target = e && e.target as HTMLInputElement;
        if (!target) {
            return;
        }

        let needsSave = false;
        let cameraIconId = `${ target.getAttribute("data-id") }_camera`;
        let cameraIconDivElement = document.getElementById(cameraIconId);
        let removeCameraIcon = () => {
            let parentElement = cameraIconDivElement?.parentElement;
            parentElement && this.removeImage(parentElement, cameraIconDivElement);
        };

        let defectId = target.getAttribute("data-id");
        if (defectId) {
            let repairStatus = target.getAttribute("data-status") as TRepairStatus;
            let defectToRepair = this.model.getDefectToRepair(defectId);

            if (repairStatus && defectToRepair) {
                needsSave = repairStatus !== "NotRepaired";
                defectToRepair.repairStatus = repairStatus;
                if (repairStatus === "Repaired") {
                    if (this.showCameraIcons && this.canUseMedia) {
                        let repairContainerOption = target.parentElement?.parentElement;
                        if (repairContainerOption && !cameraIconDivElement) {
                            cameraIconDivElement = createElement("span", "cameraRepairIcon", cameraIconId);
                            cameraIconDivElement.innerHTML = `<svg class="svgIcon bigger" role="img" aria-label="Camera icon">
                                                                <use xlink:href="#drive-camera"></use>
                                                            </svg>`;
                            cameraIconDivElement.onclick = event => {
                                openPictureOptionDialog(event, this.app.store, cameraIconId, this.translator, this.model.getAssetType() === "Trailer");
                            };
                            repairContainerOption.appendChild(cameraIconDivElement);
                        }
                    }
                } else {
                    removeCameraIcon();
                }
            }
        }
        this.toggleRepairSaveButton(needsSave);
    }

    private logInteraction() {
        // Just to log the event
    }

    private getExistingIncompleteDvir() {
        let dvirCollection = this.app.dvirLogsService.getDvirLogs();
        for (let i = 0; i < dvirCollection.length; i++) {
            let dvirLog: DVIRLog = dvirCollection[i];
            if (dvirLog.getResource().id === (this.model || this.newInspection).getResource().id && !dvirLog.isComplete(this.app)) {
                return dvirLog;
            }
        }

        return null;
    }

    private changeAsset() {
        this.goBack(undefined, true);
    }

    private fetchAndPopulate(model: DVIRLog) {
        this.app.dvirLogsService.resetProperties(model);
        this.populateAssets(model.getResource().id, model);
    }

    private updatePageStateBasedOnVehicle() {
        let licenseState = getDeviceLicense(this.app.store, !this.newInspection && this.model ? this.model._device : undefined);
        if (licenseState) {
            this.isCanada = isFromCanada(licenseState);
            this.isAustralia = isFromAustralia(licenseState);
        }
    }

    private showOnDutyToastMsg(onLogCreated: boolean) {
        onLogCreated && toastr.success(this.getString("You have been placed on duty."));
    }
}
